/**
 * Utility functions for handling refresh token errors consistently across the application
 */

/**
 * Checks if an error is a refresh token error
 */
export const isRefreshTokenError = (error: any): boolean => {
  const errorMessage = error?.message || error?.toString() || '';
  return errorMessage.includes('Missing Refresh Token') ||
         errorMessage.includes('refresh token') ||
         errorMessage.includes('Refresh Token') ||
         errorMessage.includes('refresh_token') ||
         errorMessage.includes('offline_access') ||
         errorMessage.includes('api/v2/');
};

/**
 * Handles an error and returns appropriate error message
 */
export const handleApiError = (error: any, defaultMessage: string = 'An error occurred', endpoint?: string): string => {
  // Import logger dynamically to avoid circular imports
  import('./logger').then(({ frontendLogger }) => {
    // Log structured error info
    frontendLogger.logApiError(error, endpoint || 'unknown', {
      is_refresh_token_error: isRefreshTokenError(error)
    });

    if (isRefreshTokenError(error)) {
      frontendLogger.warn('Session expired - user needs to re-authenticate', 'SessionExpired');
    }
  });

  if (isRefreshTokenError(error)) {
    return 'Session expired. Please logout and login again.';
  }

  // Handle 503 Service Unavailable errors (high load/database issues)
  if (error?.response?.status === 503) {
    return error?.response?.data?.detail || 'We\'re experiencing high load at the moment. Please try again in a few seconds.';
  }

  if (error?.response?.status === 413) {
    return (
      error?.response?.data?.detail ||
      'The geofence file is too large for the server to accept. Ask your administrator to raise the upload size limit, or reduce the file size.'
    );
  }

  return defaultMessage;
};

/**
 * Dispatches a custom event for refresh token errors
 */
export const dispatchRefreshTokenError = (): void => {
  window.dispatchEvent(new CustomEvent('refreshTokenError', {
    detail: { message: 'Session expired. Please logout and login again.' }
  }));
};
